'use client';

import { useEffect, useRef, type RefObject } from 'react';

import { trackItemImpressions } from '@/lib/public-menu-analytics';

// Tracks menu-item impressions for the public menu without touching the template
// engines. Both the legacy-scroll and paged-flip renderers stamp a stable
// `data-item-id` onto every item repeat root, so a single IntersectionObserver
// over the menu container catches real on-screen views regardless of which engine
// produced the markup. Each item is reported at most once per mount.
//
// Impressions are coalesced into a single debounced flush so a fast scroll does
// not spawn one network request per card.
export function useMenuItemImpressionTracker(
  containerRef: RefObject<HTMLElement | null>,
  menuId: string | null | undefined,
) {
  const seen = useRef<Set<string>>(new Set());
  const pending = useRef<Array<{ menuItemId: string }>>([]);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !menuId) return;

    const flush = () => {
      flushTimer.current = null;
      if (pending.current.length === 0) return;
      const impressions = pending.current;
      pending.current = [];
      trackItemImpressions(menuId, impressions);
    };

    const scheduleFlush = () => {
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(flush, 400);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target as HTMLElement;
          const id = el.getAttribute('data-item-id');
          if (!id || seen.current.has(id)) continue;
          seen.current.add(id);
          pending.current.push({ menuItemId: id });
          observer.unobserve(el);
        }
        scheduleFlush();
      },
      { threshold: 0.5 },
    );

    const observeAll = () => {
      container.querySelectorAll<HTMLElement>('[data-item-id]').forEach((el) => {
        const id = el.getAttribute('data-item-id');
        if (id && !seen.current.has(id)) observer.observe(el);
      });
    };

    observeAll();

    // Re-scan when the rendered tree changes (category filter, search results,
    // paged-flip page turn). Debounced so animation attribute churn is cheap.
    let mutationScheduled = false;
    const mutation = new MutationObserver(() => {
      if (mutationScheduled) return;
      mutationScheduled = true;
      setTimeout(() => {
        mutationScheduled = false;
        observeAll();
      }, 150);
    });
    mutation.observe(container, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutation.disconnect();
      if (flushTimer.current) clearTimeout(flushTimer.current);
      flush();
    };
  }, [containerRef, menuId]);
}
